import React from 'react';
import { useSelector } from 'react-redux';
import { Step, StepList, Steps } from '../../../CommonComponents/OPL/Stepper';
import { WORKFLOW_PADDING, WORKFLOW_WIDTH } from '../../Workflow.meta';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { PageId, WorkflowIdEnum } from '../../api/useWorkflowDataQuery';
import { getSteps, StepInfo } from './WorkflowStepper.meta';
import { StepperLoading } from '../../../CommonComponents/OPL/Stepper/Stepper.loading';
import { useWorkflowNavigateToMutation } from '../../api/useWorkflowNavigateToMutation';
import { FullPageLoading } from '../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { toast } from '../../../CommonComponents/OPL/Toast/Toast';
import { MESSAGES } from '../../../CommonConstants';
import { selectAssetBucketingHeadline } from '../../../CommonSelectors/CommonAreaSelector';
import { TextLoading } from '../../../CommonComponents/OPL/Text/Text.loading';

interface Props {
  clientId?: string;
  workflowId?: WorkflowIdEnum;
  isLoading?: boolean;
  pageId?: PageId;
}

export const WorkflowStepper: React.FC<Props> = ({
  clientId,
  workflowId,
  isLoading,
  pageId = PageId.Start,
}) => {
  const { mutateAsync: navigateTo, isLoading: isNavigating } = useWorkflowNavigateToMutation({
    clientId: clientId || '',
  });

  const assetBucketingHeadline = useSelector(selectAssetBucketingHeadline);

  const onStepSelect = async (step: StepInfo) => {
    if (!clientId) {
      return;
    }

    try {
      const { Route } = await navigateTo({
        clientId,
        pageId: step.navigateTo,
        workflowId: workflowId ?? WorkflowIdEnum.Comprehensive,
      });

      if (Route.Name === 'Workflow') {
        ADV.util.Router.go2('Workflow', Route.Params);
      }
    } catch (error) {
      console.error(error);

      toast.error(MESSAGES.GENERIC_ERROR);
    }
  };

  const steps = getSteps(workflowId ?? WorkflowIdEnum.Comprehensive);

  const currentStepIndex = steps.findIndex(step => step.pages.includes(pageId));

  const WORKFLOW_HEADER_MAP: Record<WorkflowIdEnum, { title: string; description?: string }> = {
    [WorkflowIdEnum.Comprehensive]: { title: 'Comprehensive Plan' },
    [WorkflowIdEnum.AssetBucketing]: { title: assetBucketingHeadline },
    [WorkflowIdEnum.EstatePlanning]: {
      title: 'Build an estate plan to fit your unique needs',
      description:
        'Provide detailed information about your household, finances, and estate to create a comprehensive overview of your plan',
    },
  };

  const workflowHeader = WORKFLOW_HEADER_MAP[workflowId ?? WorkflowIdEnum.Comprehensive];

  return (
    <>
      {isNavigating && <FullPageLoading />}

      <Box as="header" width={WORKFLOW_WIDTH} px={WORKFLOW_PADDING} my="48" mx="auto">
        {isLoading ? (
          <TextLoading width={200} height="h4" mb="8" />
        ) : (
          <>
            <Text as="p" fontSize="h4" fontWeight={500} mb="8">
              {workflowHeader.title}
            </Text>

            {workflowHeader.description && (
              <Text as="p" color="neutral.500" mb="24">
                {workflowHeader.description}
              </Text>
            )}
          </>
        )}

        {isLoading ? (
          <StepperLoading stepCount={steps.length} />
        ) : (
          <Steps
            selectedIndex={currentStepIndex}
            onSelect={stepIndex => {
              onStepSelect(steps[stepIndex]);
            }}
          >
            <StepList aria-hidden>
              {steps.map(({ label }, index) => (
                <Step key={label} disabled={currentStepIndex < index}>
                  {label}
                </Step>
              ))}
            </StepList>
          </Steps>
        )}
      </Box>
    </>
  );
};
